/**
 * =============================================================================
 * USE REPLY SUBMISSION HOOK - GAP-006 (+ PR5-remainder reply images)
 * =============================================================================
 *
 * Manages reply submission state for the ReplyForm component: create the reply,
 * then (PR5-remainder, ADR-022 §12 PR5) upload any attached photos against the
 * reply-scoped upload capability the create response carries.
 *
 * SPEC REFERENCE: GAP-006-widget-reply-ui-SPEC.md §3.5; ADR-022 §8/§12 PR5
 *
 * FAIL-OPEN / NEVER-A-LOST-REPLY (ADR-022 §8, BL-WIDGET-UPLOAD-FAILURE-UX-001):
 * image handling must never abort the reply. If the reply is created but one or
 * more photos fail to attach, the hook lands in a recoverable `partial` state —
 * NOT `success`, and `onSuccess` does NOT fire — mirroring the question path.
 *
 * NO-LOG / NO-LEAK (ADR-024 §10.5): the opaque upload-proof handle is held in a
 * ref (memory only), sent solely as `X-Upload-Proof` by the API client, and is
 * never exposed in the hook's return value, logged, persisted, or placed in a URL.
 *
 * =============================================================================
 */

import { useState, useCallback, useRef } from 'react';
import { widgetApi } from '../api/client';
import { invalidatePublicReads } from '../api/publicQuestionCache';
import {
  runImageUploadSequence,
  notifyHostCallback,
  type FailedImageUpload,
} from '../lib/imageUpload';
import { logger } from '../lib/logger';

// =============================================================================
// TYPES
// =============================================================================

export interface ReplyFormData {
  body: string;
  authorName: string;
  authorEmail: string;
  /** Explicit public-display consent (BL-PRIVACY-CONSENT-CAPTURE-001). */
  consentToPublicDisplay: boolean;
}

/**
 * `partial` = the reply was created and is pending review, but at least one
 * attached photo failed to upload. A recoverable state, never the success
 * screen, never fires `onSuccess` (BL-WIDGET-UPLOAD-FAILURE-UX-001).
 */
export type ReplyFormStatus = 'idle' | 'submitting' | 'success' | 'partial' | 'error';

export interface UseReplySubmissionOptions {
  questionId: string;
  onSuccess?: (replyId: string) => void;
  onError?: (error: Error) => void;
}

export interface UseReplySubmissionResult {
  /** Submit a reply, optionally with photos to attach. */
  submit: (data: ReplyFormData, turnstileToken: string, images?: File[]) => Promise<void>;
  /** Current submission status */
  status: ReplyFormStatus;
  /** Error message if the reply itself failed (sanitized) */
  error: string | null;
  /** Reply ID after the reply is created (present in success/partial states) */
  replyId: string | null;
  /**
   * Photos that failed to attach, in the `partial` state. Retains each `File`
   * so a retry re-sends only the failed ones without re-selecting.
   */
  failedImages: FailedImageUpload[];
  /** Whether a partial-state retry is in flight. */
  isRetrying: boolean;
  /**
   * Determinate image-upload progress while `submitting`, or `null` when no
   * photos are uploading. Drives the reply form's progress bar so a multi-photo
   * upload on a slow connection isn't a silent, indeterminate "Submitting…"
   * (a11y parity with the question path).
   */
  uploadProgress: { current: number; total: number } | null;
  /** Retry ONLY the failed photos against the already-created reply. */
  retryFailedImages: () => Promise<void>;
  /** Accept the reply without the photos that failed (partial → success). */
  continueWithoutImages: () => void;
  /** Reset the form to idle state */
  reset: () => void;
}

// =============================================================================
// ERROR SANITIZATION
// =============================================================================

/**
 * User-friendly error messages mapped from API error codes.
 * NEVER display raw backend error messages — always map through this object.
 */
const REPLY_ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Unable to submit reply. Please check your input and try again.',
  NOT_FOUND: 'This question is no longer available for replies.',
  // BL-REV-GAP006-001: distinct from VALIDATION_ERROR so the state-specific
  // message is shown instead of the generic "check your input" guidance.
  QUESTION_NOT_ACCEPTING_REPLIES: 'This question is no longer accepting replies.',
  RATE_LIMITED: 'Too many replies. Please wait before trying again.',
  SERVICE_UNAVAILABLE: 'Verification is temporarily unavailable. Please try again.',
  NETWORK_ERROR: 'Unable to connect. Please check your connection.',
  OFFLINE: 'You appear to be offline. Please check your connection.',
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',
  MAX_RETRIES_EXCEEDED: 'The server is temporarily unavailable. Please try again later.',
  WIDGET_INIT_ERROR: 'Unable to submit: widget not properly configured.',
};

/**
 * Sanitize an API error response into a user-friendly message.
 * NEVER display raw backend error messages — always map through this function.
 */
export function sanitizeReplyError(errorCode: string, _backendMessage: string): string {
  return REPLY_ERROR_MESSAGES[errorCode] ?? 'Something went wrong. Please try again.';
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for managing reply submission state.
 *
 * Handles:
 * - POST /api/public/questions/:questionId/replies (create the reply)
 * - PR5-remainder: sequential compress + upload of attached photos against the
 *   reply-scoped upload capability, with fail-open partial-failure recovery
 * - Loading / success / partial / error state management
 * - Error handling with sanitized user-friendly messages
 *
 * @param options - Submission options including questionId and callbacks
 * @returns Submission controls and state
 *
 * @example
 * ```tsx
 * const { submit, status, error, replyId, reset } = useReplySubmission({
 *   questionId: 'q_abc123',
 *   onSuccess: (id) => console.log(`Reply ${id} submitted`),
 * });
 * ```
 */
export function useReplySubmission(
  options: UseReplySubmissionOptions
): UseReplySubmissionResult {
  const { questionId, onSuccess, onError } = options;

  const [status, setStatus] = useState<ReplyFormStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [replyId, setReplyId] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<FailedImageUpload[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<
    { current: number; total: number } | null
  >(null);

  // ADR-024 §11 opaque upload-proof handle: memory-only, never exposed/logged/
  // URL-embedded. A ref (not state) keeps it out of the render/return surface.
  const uploadProofRef = useRef<string | undefined>(undefined);

  // Synchronous re-entry guard: the disabled submit button relies on a committed
  // re-render, so a rapid double-click or a programmatic double-dispatch could
  // otherwise enter submit() twice and create DUPLICATE replies before the
  // `submitting` state lands. Set before any await, cleared once terminal.
  const submitInFlightRef = useRef(false);
  // Same guard for the retry leg: each image POST consumes one server-side
  // capability use, so two same-tick retries would double-spend uses / duplicate
  // uploads. `isRetrying` (React state) alone doesn't commit synchronously.
  const retryInFlightRef = useRef(false);

  const submit = useCallback(
    async (data: ReplyFormData, turnstileToken: string, images: File[] = []): Promise<void> => {
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      setStatus('submitting');
      setError(null);
      setFailedImages([]);
      setUploadProgress(null);

      // Set only inside the try's success branch; the terminal onSuccess is
      // invoked AFTER the try so a throwing host callback can never be caught
      // by the catch below and flip a fully-created, fully-uploaded reply to
      // `error` (never mis-report a created reply as failed — the retry /
      // continue paths already call onSuccess outside any try). Folds external
      // anchor-first review F1.
      let createdReplyId: string | null = null;

      try {
        const response = await widgetApi.replies.submit(questionId, {
          body: data.body.trim(),
          authorName: data.authorName.trim(),
          authorEmail: data.authorEmail.trim(),
          turnstileToken,
          consentToPublicDisplay: data.consentToPublicDisplay, // BL-PRIVACY-CONSENT-CAPTURE-001
        });

        if (!response.success) {
          const sanitizedMessage = sanitizeReplyError(
            response.error.code,
            response.error.message
          );
          setStatus('error');
          setError(sanitizedMessage);
          // Wrapped: a throwing host onError here must not re-enter the catch below
          // and fire onError a SECOND time (with the generic fallback), nor escape
          // as an unhandled rejection.
          notifyHostCallback(() => onError?.(new Error(sanitizedMessage)));
          return;
        }

        // ADR-028 Slice 3: the reply now durably exists, so retained public
        // browse/detail state is stale and in-flight reads are fenced against
        // repopulating it. This sits at the CREATION boundary, before the
        // optional image uploads below — a partial photo failure does not undo
        // the created reply. A failed create returns above and never reaches
        // here. Nothing is inserted: the reply awaits moderation and must not
        // be represented as published.
        invalidatePublicReads();

        const newReplyId = response.data.replyId;
        setReplyId(newReplyId);
        // ADR-024 §11: reply-scoped upload-proof for the immediate image legs.
        // Optional in the response (version coexistence + tenant may have reply
        // images disabled); a missing handle still lets non-ITP browsers upload
        // via the guest cookie, and otherwise the server rejects gracefully.
        uploadProofRef.current = response.data.uploadCapability?.token;

        // Upload any attached photos independently — a failure is collected
        // (with the File retained for retry) rather than aborting the reply.
        // No photos → nothing to upload; the reply succeeds without touching the
        // image API at all (the common text-only reply path).
        const failed =
          images.length > 0
            ? await runImageUploadSequence(
                widgetApi.images.uploadForReply,
                newReplyId,
                images,
                uploadProofRef.current,
                // Drive the determinate progress bar ("Uploading photo X of N").
                (current, total) => setUploadProgress({ current, total })
              )
            : [];

        // Uploads finished (or none) — drop the progress indicator before the
        // terminal state renders.
        setUploadProgress(null);

        if (failed.length > 0) {
          // Partial failure: the reply is real, but photos were dropped. Do NOT
          // show success and do NOT call onSuccess — surface a recoverable state.
          // Log each drop for parity with the question path (dev diagnostics;
          // console is stripped by drop_console in prod). NEVER log the proof —
          // only the filename + the (already user-facing) failure message.
          failed.forEach((item) =>
            logger.error('IMAGE_UPLOAD_FAILURE', null, {
              context: 'reply',
              fileName: item.name,
              errorMessage: item.message,
            })
          );
          setFailedImages(failed);
          setStatus('partial');
        } else {
          // All photos attached (or none) — the proof is spent; drop it.
          uploadProofRef.current = undefined;
          setStatus('success');
          createdReplyId = newReplyId;
        }
      } catch {
        const fallbackMessage = 'Something went wrong. Please try again.';
        setUploadProgress(null);
        setStatus('error');
        setError(fallbackMessage);
        notifyHostCallback(() => onError?.(new Error(fallbackMessage)));
      } finally {
        // Terminal (success/partial/error): allow a fresh submit again. Cleared
        // here rather than after onSuccess so a host callback that throws still
        // releases the guard.
        submitInFlightRef.current = false;
      }

      // Outside the try (see createdReplyId): a host onSuccess throw must not
      // undo the reply's `success` state, nor escape as an unhandled rejection.
      // Null unless the reply was created and every attached photo attached.
      if (createdReplyId !== null) {
        const id = createdReplyId;
        notifyHostCallback(() => onSuccess?.(id));
      }
    },
    [questionId, onSuccess, onError]
  );

  /**
   * Retry only the photos that failed, against the already-created reply. Never
   * re-submits the reply and never requires a fresh Turnstile token. Re-uses the
   * same in-memory upload-proof; if it has expired the upload fails gracefully
   * back into the still-failed list (BL-WIDGET-UPLOAD-FAILURE-UX-001).
   */
  const retryFailedImages = useCallback(async (): Promise<void> => {
    // Synchronous guard (ref, not the async `isRetrying` state) so a same-tick
    // double-dispatch can't start two capability-use-spending retry sequences.
    if (!replyId || retryInFlightRef.current) return;
    retryInFlightRef.current = true;
    setIsRetrying(true);
    let succeeded = false;
    try {
      const stillFailed = await runImageUploadSequence(
        widgetApi.images.uploadForReply,
        replyId,
        failedImages.map((item) => item.file),
        uploadProofRef.current
      );
      if (stillFailed.length === 0) {
        setFailedImages([]);
        // Every photo attached — the proof is spent; drop it (minimize lifetime).
        uploadProofRef.current = undefined;
        setStatus('success');
        succeeded = true;
      } else {
        setFailedImages(stillFailed);
      }
    } finally {
      // `finally` (not a trailing statement) so a stuck `isRetrying`/guard can't
      // permanently disable the recovery buttons even if the sequence ever threw.
      setIsRetrying(false);
      retryInFlightRef.current = false;
    }
    // Outside the try/finally: a throwing host onSuccess can't wedge the guard
    // (notifyHostCallback also keeps it from escaping as an unhandled rejection).
    if (succeeded) notifyHostCallback(() => onSuccess?.(replyId));
  }, [replyId, failedImages, onSuccess]);

  /**
   * Accept the reply without the photos that failed (partial → success). The
   * reply is real and pending review; this fires `onSuccess` since the user has
   * chosen to proceed (BL-WIDGET-UPLOAD-FAILURE-UX-001).
   */
  const continueWithoutImages = useCallback((): void => {
    if (!replyId) return;
    setFailedImages([]);
    // Terminal: the user is done with photos — the proof is no longer needed.
    uploadProofRef.current = undefined;
    setStatus('success');
    notifyHostCallback(() => onSuccess?.(replyId));
  }, [replyId, onSuccess]);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setReplyId(null);
    setFailedImages([]);
    setIsRetrying(false);
    setUploadProgress(null);
    uploadProofRef.current = undefined;
    // Defense-in-depth (F-reset-refs): the finally blocks already clear these on
    // every terminal path, but clear them here too so the guards can never wedge
    // if reset() is ever reachable mid-flight after a refactor.
    submitInFlightRef.current = false;
    retryInFlightRef.current = false;
  }, []);

  return {
    submit,
    status,
    error,
    replyId,
    failedImages,
    isRetrying,
    uploadProgress,
    retryFailedImages,
    continueWithoutImages,
    reset,
  };
}
